import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { meHooks } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Modal from '../../components/ui/Modal.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  gstin: z.string().optional(),
  address: z.string().optional(),
  contactPerson: z.string().optional(),
  contactEmail: z.string().email('Invalid email').optional().or(z.literal('')),
});

export default function MerchantExportersPage() {
  const { data: mes = [], isLoading } = meHooks.useList({ includeInactive: 'true' });
  const createMut = meHooks.useCreate();
  const updateMut = meHooks.useUpdate();
  const deleteMut = meHooks.useDelete();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  const openCreate = () => { setEditing(null); reset({}); setModalOpen(true); };
  const openEdit = (me) => { setEditing(me); reset(me); setModalOpen(true); };
  const onSubmit = async (values) => {
    if (editing) await updateMut.mutateAsync({ id: editing.id, ...values });
    else await createMut.mutateAsync(values);
    setModalOpen(false); reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Merchant Exporters</h2>
          <p className="text-sm text-gray-500">Manage third-party merchant exporters</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add Merchant Exporter</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Name','GSTIN','Contact Person','Email','Status',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {mes.map(me => (
                  <tr key={me.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{me.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{me.gstin || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{me.contactPerson || '—'}</td>
                    <td className="px-4 py-3 text-gray-500">{me.contactEmail || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={"badge " + (me.isActive ? 'badge-approved' : 'badge-cancelled')}>
                        {me.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => openEdit(me)} className="text-gray-400 hover:text-blue-600"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteTarget(me)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Merchant Exporter' : 'Add Merchant Exporter'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Name" required error={errors.name?.message} {...register('name')} />
          <Input label="GSTIN" placeholder="22AAAAA0000A1Z5" {...register('gstin')} />
          <Input label="Address" {...register('address')} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Contact Person" {...register('contactPerson')} />
            <Input label="Contact Email" type="email" error={errors.contactEmail?.message} {...register('contactEmail')} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>{editing ? 'Save Changes' : 'Add'}</Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={async () => { await deleteMut.mutateAsync(deleteTarget.id); setDeleteTarget(null); }}
        loading={deleteMut.isPending} title="Delete Merchant Exporter"
        message={`Delete "${deleteTarget?.name}"? Existing records are preserved.`} confirmLabel="Delete"
      />
    </div>
  );
}
